import { useState } from "react";
import { motion } from "framer-motion";
import { Play, X, Lock } from "lucide-react";

const easeOut = [0.16, 1, 0.3, 1];

export default function VideoCard({ video, large = false, onLockClick, allVideos = [] }) {
    const [open, setOpen] = useState(false);
    const locked = video.locked;

    // "Watch next" suggestions — prefer same region, exclude self, cap at 3
    const watchNext = (() => {
        if (!allVideos || allVideos.length === 0) return [];
        const others = allVideos.filter((v) => v.id !== video.id && !v.locked);
        const sameRegion = others.filter((v) => v.region === video.region);
        const rest = others.filter((v) => v.region !== video.region);
        return [...sameRegion, ...rest].slice(0, 3);
    })();

    return (
        <>
            <motion.button
                onClick={() => locked ? onLockClick?.() : setOpen(true)}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-10% 0px" }}
                transition={{ duration: 0.7, ease: easeOut }}
                whileHover={{ y: -4 }}
                className={`group relative w-full overflow-hidden border border-[var(--sb-border)] hover:border-[var(--sb-brand)] transition-[border-color,box-shadow] duration-500 text-left hover:shadow-[0_20px_60px_-20px_rgba(107,167,222,0.35)] ${large ? "aspect-[16/10]" : "aspect-video"}`}
                data-testid={`video-card-${video.id}`}
            >
                <img
                    src={video.thumb}
                    alt={video.title}
                    className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-[1.08]"
                    loading="lazy"
                />
                {/* Base image overlay for text legibility */}
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)] via-[var(--sb-bg)]/45 to-transparent" />
                {/* Extra hover tint */}
                <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500" style={{ background: "radial-gradient(50% 55% at 50% 55%, rgba(107,167,222,0.18) 0%, rgba(0,0,0,0) 65%)" }} />

                {locked ? (
                    <div className="absolute inset-0 flex items-center justify-center bg-[var(--sb-bg)]/60 backdrop-blur-sm">
                        <div className="flex flex-col items-center gap-3 text-center px-6">
                            <Lock size={26} className="text-[var(--sb-brand)]" />
                            <div className="overline">{video.lock_reason === "login_required" ? "Free with account" : "Subscriber only"}</div>
                            <div className="font-serif text-xl text-[var(--sb-text)] leading-tight">{video.title}</div>
                            <div className="text-sm text-[var(--sb-text-dim)]">
                                {video.lock_reason === "login_required" ? "Sign in or register (free)" : "£1.89 to unlock"}
                            </div>
                        </div>
                    </div>
                ) : (
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                        <motion.div
                            initial={false}
                            animate={{ scale: 0.6, opacity: 0 }}
                            whileHover={{ scale: 1, opacity: 1 }}
                            className="w-16 h-16 rounded-full bg-[var(--sb-brand)] flex items-center justify-center shadow-[0_10px_40px_rgba(107,167,222,0.55)] opacity-0 group-hover:opacity-100 group-hover:scale-100 transition-[opacity,transform] duration-500"
                            style={{ transform: "scale(0.7)" }}
                        >
                            <Play size={24} className="text-[var(--sb-bg)] ml-1" fill="currentColor" />
                        </motion.div>
                    </div>
                )}

                <div className="absolute left-0 right-0 bottom-0 p-6 lg:p-7">
                    {!locked && (
                        <div className="flex items-end justify-between gap-3">
                            <div className="min-w-0">
                                <div className="overline mb-2">{video.region} · {video.duration}</div>
                                <div
                                    className={`font-serif text-[var(--sb-text)] leading-[1.05] tracking-tight transition-transform duration-500 group-hover:-translate-y-0.5 ${
                                        large ? "text-3xl lg:text-5xl" : "text-2xl lg:text-3xl"
                                    }`}
                                >
                                    {video.title}
                                </div>
                                {large && <p className="text-sm text-[var(--sb-text-dim)] mt-2 max-w-md">{video.summary}</p>}
                            </div>
                            {video.exclusive && (
                                <span className="text-[10px] tracking-[0.22em] uppercase border border-[var(--sb-brand)] text-[var(--sb-brand)] px-2 py-1 backdrop-blur-sm bg-[var(--sb-bg)]/40 shrink-0">
                                    Exclusive
                                </span>
                            )}
                        </div>
                    )}
                </div>
            </motion.button>

            {open && !locked && (
                <div className="fixed inset-0 z-[60] bg-[var(--sb-bg)]/90 backdrop-blur-md flex items-center justify-center p-4 overflow-y-auto" onClick={() => setOpen(false)} data-testid={`video-modal-${video.id}`}>
                    <button onClick={() => setOpen(false)} className="absolute top-6 right-6 text-[var(--sb-text)] hover:text-[var(--sb-brand)] z-[61]" data-testid="video-modal-close">
                        <X size={28} />
                    </button>
                    <div className="w-full max-w-5xl my-8" onClick={(e) => e.stopPropagation()}>
                        <div className="aspect-video bg-black">
                            <iframe
                                src={`https://www.youtube.com/embed/${video.video_id}?autoplay=1&rel=0`}
                                title={video.title}
                                className="w-full h-full"
                                frameBorder="0"
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                            />
                        </div>

                        {watchNext.length > 0 && (
                            <div className="mt-8" data-testid="watch-next">
                                <div className="flex items-center gap-3 mb-4">
                                    <span className="w-8 h-px bg-[var(--sb-brand)]" />
                                    <span className="overline">Watch next</span>
                                </div>
                                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                                    {watchNext.map((v) => (
                                        <button
                                            key={v.id}
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                // Trigger a same-tab replay by dispatching a custom event Gallery-agnostic:
                                                window.location.hash = `video-${v.id}`;
                                                setOpen(false);
                                            }}
                                            className="group text-left border border-[var(--sb-border)] hover:border-[var(--sb-brand)] transition-colors overflow-hidden"
                                            data-testid={`watch-next-${v.id}`}
                                        >
                                            <div className="relative aspect-video overflow-hidden">
                                                <img src={v.thumb} alt={v.title} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
                                                <div className="absolute inset-0 bg-gradient-to-t from-[var(--sb-bg)] to-transparent opacity-70" />
                                                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                                                    <Play size={22} className="text-[var(--sb-text)]" fill="currentColor" />
                                                </div>
                                            </div>
                                            <div className="p-4">
                                                <div className="overline mb-1.5">{v.region} · {v.duration || ""}</div>
                                                <div className="font-serif text-lg leading-tight text-[var(--sb-text)] group-hover:text-[var(--sb-brand)] transition-colors line-clamp-2">
                                                    {v.title}
                                                </div>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </>
    );
}
